import { useState, useMemo } from "react";
import { Dumbbell, TrendingUp, StickyNote } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { images } from "@/data/images";
import { playerDevelopment } from "@/data/playerDev";

const groups = ["All", "Positions", "Skills", "Performance"];

export default function PlayerDevelopment() {
  const [active, setActive] = useState("All");

  const filtered = useMemo(
    () =>
      active === "All"
        ? playerDevelopment
        : playerDevelopment.filter((p) => p.group === active),
    [active]
  );

  return (
    <div data-testid="player-development-page">
      <PageHeader
        eyebrow="Player Development"
        title="Develop the whole athlete."
        intro="Position-specific work, core skills, and performance — with drills, progressions, and coaching notes. We develop the player and the person."
        image={images.coachStretch}
      />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <button
                  key={g}
                  data-testid={`devgroup-filter-${g.toLowerCase()}`}
                  onClick={() => setActive(g)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border ${
                    active === g
                      ? "bg-burnt border-burnt text-white"
                      : "bg-transparent border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.06}>
                <div
                  data-testid={`devcard-${p.id}`}
                  className="border border-white/10 bg-ink-surface p-7 h-full hover:border-white/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">
                      {p.group}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-white mt-3">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-zinc-500 leading-relaxed">{p.summary}</p>

                  <div className="mt-5 space-y-4">
                    <div>
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">
                        <Dumbbell size={14} className="text-burnt" /> Drills
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {p.drills.map((d, j) => (
                          <span
                            key={j}
                            className="text-xs text-zinc-300 border border-white/10 px-2.5 py-1"
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">
                        <TrendingUp size={14} className="text-burnt" /> Progressions
                      </p>
                      <ul className="space-y-1">
                        {p.progressions.map((pr, j) => (
                          <li key={j} className="text-sm text-zinc-500">
                            {pr}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">
                        <StickyNote size={14} className="text-burnt" /> Coaching Notes
                      </p>
                      <p className="text-sm text-zinc-400 leading-relaxed italic">
                        {p.notes}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
